import React, { useState, useRef, useEffect } from "react";
import { Camera, X, Loader2, Send, Wand2, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_TYPES = ["Desktop", "Laptop", "UPS", "Monitor", "Printer", "Tablet", "Other"];
const FALLBACK_SOURCES = ["Facebook", "Instagram", "Just Dial", "Other"];
const MAX_PHOTOS = 6;

const EMPTY = {
  item_type: "", brand: "", model: "", code: "", name: "", qty: "1", price: "",
  purchase_date: "", purchase_amount: "", contact_name: "", contact_mobile: "", enquiry_source: "",
};

const inputCls = "w-full rounded-lg border px-3.5 py-2.5 text-[15px] outline-none transition-shadow focus:ring-4";
const inputStyle = { background: "#fff", borderColor: "#d8c8af", color: "#1F4A38" };
const labelCls = "grid gap-1 font-heading text-[13.5px] font-semibold";
const labelStyle = { color: "var(--green-950)" };

export async function compressImage(file, maxDim = 900, quality = 0.72) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
}

export const StockForm = ({ headers, onSuccess }) => {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [error, setError] = useState("");
  const [types, setTypes] = useState(FALLBACK_TYPES);
  const [sources, setSources] = useState(FALLBACK_SOURCES);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/stock/options`)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d.item_types) && d.item_types.length) setTypes(d.item_types);
        if (Array.isArray(d.enquiry_sources) && d.enquiry_sources.length) setSources(d.enquiry_sources);
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const genCode = async () => {
    if (!form.item_type) { setError("Select Item Type first to generate a code."); return; }
    setGenBusy(true); setError("");
    try {
      const qs = new URLSearchParams({ item_type: form.item_type, brand: form.brand });
      const r = await fetch(`${API}/stock/next_code?${qs}`, { headers });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not generate code");
      setForm((f) => ({ ...f, code: data.code }));
    } catch (err) {
      setError(err.message);
    } finally {
      setGenBusy(false);
    }
  };

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const compressed = await Promise.all(files.slice(0, MAX_PHOTOS).map((f) => compressImage(f)));
      setPhotos((p) => [...p, ...compressed].slice(0, MAX_PHOTOS));
    } catch {
      setError("Could not read that photo. Please try another one.");
    }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/stock/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({
          ...form,
          qty: parseInt(form.qty, 10) || 1,
          price: parseInt(form.price, 10) || 0,
          purchase_amount: parseInt(form.purchase_amount, 10) || 0,
          photos,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not save item");
      setForm(EMPTY); setPhotos([]);
      onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="stock-entry-form" className="grid gap-3">
      <label className={labelCls} style={labelStyle}>
        <span>Item Type <span style={{ color: "var(--orange-600)" }}>*</span></span>
        <select value={form.item_type} onChange={set("item_type")} required data-testid="stock-item-type"
                className={inputCls} style={inputStyle}>
          <option value="" disabled>Select type…</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className={labelCls} style={labelStyle}>
          <span>Brand</span>
          <input value={form.brand} onChange={set("brand")} placeholder="Dell / HP / APC" data-testid="stock-brand"
                 className={inputCls} style={inputStyle} />
        </label>
        <label className={labelCls} style={labelStyle}>
          <span>Model</span>
          <input value={form.model} onChange={set("model")} placeholder="Latitude 5420" data-testid="stock-model"
                 className={inputCls} style={inputStyle} />
        </label>
      </div>

      <label className={labelCls} style={labelStyle}>
        <span>Item Code</span>
        <div className="flex gap-2">
          <input value={form.code} onChange={set("code")} placeholder="LAPDL100" data-testid="stock-code"
                 className={`${inputCls} uppercase`} style={inputStyle} />
          <button type="button" onClick={genCode} disabled={genBusy || !form.item_type} data-testid="stock-code-generate"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-4 font-heading text-[13px] font-bold text-white disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, var(--orange-700), var(--orange-500))" }}>
            {genBusy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Code
          </button>
        </div>
      </label>

      <label className={labelCls} style={labelStyle}>
        <span>Item Name &amp; Configuration <span style={{ color: "var(--orange-600)" }}>*</span></span>
        <textarea value={form.name} onChange={set("name")} required rows={2} data-testid="stock-name"
                  placeholder="Dell Latitude 5420 i5 11th Gen / 16GB / 512GB SSD / Win 11 Pro"
                  className={`${inputCls} resize-none`} style={inputStyle} />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className={labelCls} style={labelStyle}>
          <span>Quantity <span style={{ color: "var(--orange-600)" }}>*</span></span>
          <input type="number" min="1" value={form.qty} onChange={set("qty")} required data-testid="stock-qty"
                 inputMode="numeric" className={inputCls} style={inputStyle} />
        </label>
        <label className={labelCls} style={labelStyle}>
          <span>Selling Price (₹)</span>
          <input type="number" min="0" value={form.price} onChange={set("price")} placeholder="15500" data-testid="stock-price"
                 inputMode="numeric" className={inputCls} style={inputStyle} />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-2 min-[400px]:grid-cols-2">
        <label className={labelCls} style={labelStyle}>
          <span>Purchase Date</span>
          <input type="date" value={form.purchase_date} onChange={set("purchase_date")} data-testid="stock-purchase-date"
                 className={inputCls} style={inputStyle} />
        </label>
        <label className={labelCls} style={labelStyle}>
          <span>Purchase Amount (₹)</span>
          <input type="number" min="0" value={form.purchase_amount} onChange={set("purchase_amount")} placeholder="12000"
                 inputMode="numeric" data-testid="stock-purchase-amount" className={inputCls} style={inputStyle} />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className={labelCls} style={labelStyle}>
          <span>Contact Person</span>
          <input value={form.contact_name} onChange={set("contact_name")} placeholder="Name" data-testid="stock-contact-name"
                 className={inputCls} style={inputStyle} />
        </label>
        <label className={labelCls} style={labelStyle}>
          <span>Contact Mobile</span>
          <input type="tel" value={form.contact_mobile} onChange={set("contact_mobile")} placeholder="98xxxxxxxx"
                 inputMode="tel" data-testid="stock-contact-mobile" className={inputCls} style={inputStyle} />
        </label>
      </div>

      <label className={labelCls} style={labelStyle}>
        <span>Source of Enquiry</span>
        <select value={form.enquiry_source} onChange={set("enquiry_source")} data-testid="stock-enquiry-source"
                className={inputCls} style={inputStyle}>
          <option value="">Select source…</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>

      <div>
        <span className="font-heading text-[13.5px] font-semibold" style={labelStyle}>
          Product Photos <span className="font-sans text-[12px] font-normal" style={{ color: "var(--muted)" }}>({photos.length}/{MAX_PHOTOS})</span>
        </span>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto}
               className="hidden" data-testid="stock-photo-input" />
        <input ref={galleryRef} type="file" accept="image/*" multiple onChange={onPhoto}
               className="hidden" data-testid="stock-gallery-input" />
        {photos.length > 0 && (
          <div className="mt-2 grid grid-cols-3 gap-2" data-testid="stock-photo-grid">
            {photos.map((p, i) => (
              <div key={i} className="relative overflow-hidden rounded-lg border" style={{ borderColor: "#d8c8af" }}>
                <img src={p} alt={`Product ${i + 1}`} className="h-24 w-full object-cover" />
                {i === 0 && (
                  <span className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[9px] font-extrabold text-white"
                        style={{ background: "var(--green-800)" }}>MAIN</span>
                )}
                <button type="button" onClick={() => setPhotos((ph) => ph.filter((_, j) => j !== i))}
                        data-testid={`stock-photo-remove-${i}`}
                        className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full text-white"
                        style={{ background: "rgba(31,74,56,0.85)" }}><X size={13} /></button>
              </div>
            ))}
          </div>
        )}
        {photos.length < MAX_PHOTOS && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="stock-photo-button"
                    className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-4 font-heading text-[13px] font-bold"
                    style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.55)" }}>
              <Camera size={17} style={{ color: "#E8501A" }} /> Take Photo
            </button>
            <button type="button" onClick={() => galleryRef.current?.click()} data-testid="stock-gallery-button"
                    className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-4 font-heading text-[13px] font-bold"
                    style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.55)" }}>
              <ImageIcon size={17} style={{ color: "#E8501A" }} /> Choose from Gallery
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-[13.5px] font-semibold" data-testid="stock-form-error" style={{ color: "var(--orange-700)" }}>{error}</p>}

      <button type="submit" disabled={busy} data-testid="stock-submit-button"
              className="mt-1 flex items-center justify-center gap-2 rounded-full py-3.5 font-heading text-[15.5px] font-bold text-white disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />} Save Stock Item
      </button>
    </form>
  );
};
